import { memo, useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import {
  selectIsSelectedRecordKey,
  selectIsViewingRecordID,
  setViewingRecordFromRow,
} from "@/state/redux/slices/ui";
import { useCollections } from "@/api/hooks";
import { serializeRecord } from "@/common/record";
import { getPlural, getSingular } from "@/common/intervalTypes";
import { collectionPath } from "@/common/urls";
import { indexCollections } from "@/common/utils";
import { HGCollection, HGInterval, HGIntersectionType } from "@/common/types";
import {
  BucketKind,
  GapNode,
  LevelNode,
  RecordNode,
  RecordTree,
  isLevelNode,
  safeShortName,
} from "@/common/recordTree";
import { intersectionTypeIcon } from "./IntersectionTypeIcons";
import RecordBody, { recordCardClass } from "./RecordBody";

// --- geometry ---------------------------------------------------------------
//
// One timeline, drawn top to bottom at a single x. Depth is carried by the
// line's *weight*, not by a second line: the spine is thickest along the active
// interval, narrows as the list descends into a group and again into a
// subgroup, and thickens back on the way out. Collapsing a group collapses its
// stretch of the spine along with its rows.
//
// Cards all start at CONTENT_X, so the line is the only thing that moves.
// Nothing is written to the left of the spine: the narrow strip there is for
// marks only, which is why it is barely wider than one.

const SPINE_X = 24;
const CONTENT_X = 42;
/** Right inset of a record card — lines up with the level header's rule. */
const CONTENT_R = 18;

/**
 * Half the gap between cards. Each row carries it top and bottom, so the
 * rhythm holds against a level header as evenly as against another card.
 */
const ROW_PAD_Y = 3;
/**
 * Centre of a card's first title line, from the row's top edge: the row's own
 * padding, the card's 11px, and half a 14.5px/1.28 line. Everything drawn on
 * the spine hangs off this, so the marks meet the title rather than the box.
 */
const TITLE_CY = ROW_PAD_Y + 20.5;

const SCOPE_H = 38;
const GROUP_H = 34;
const SUB_H = 24;

const SECTION_PAGE = 50;

/**
 * The spine's stroke at a given depth. The steps are deliberately coarse —
 * a half-pixel difference is not a signal anyone can read.
 */
function spineWidth(depth: number) {
  if (depth <= 0) return 3;
  if (depth === 1) return 2;
  return 1;
}

function levelHeaderHeight(depth: number) {
  if (depth <= 0) return SCOPE_H;
  return depth === 1 ? GROUP_H : SUB_H;
}

/** Where a level's own header parks, below every ancestor header. */
function levelHeaderTop(depth: number) {
  let top = 0;
  for (let d = 0; d < depth; d++) top += levelHeaderHeight(d);
  return top;
}

function levelZ(depth: number) {
  return Math.max(40 - depth * 10, 10);
}

// --- shared parts -----------------------------------------------------------

/**
 * This row's segment of the spine, drawn edge to edge of the row including the
 * gap it carries, so consecutive rows tile into one unbroken line. Per row
 * rather than per section so the stroke can change from one row to the next.
 */
function Spine({ depth, dashed }: { depth: number; dashed?: boolean }) {
  if (dashed) {
    return (
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 border-l border-dashed border-node-line"
        style={{ left: SPINE_X - 0.5 }}
      />
    );
  }
  const width = spineWidth(depth);
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute inset-y-0 ${
        depth <= 0
          ? "bg-primary/35"
          : depth === 1
            ? "bg-node-line"
            : "bg-node-line/70"
      }`}
      style={{ left: SPINE_X - width / 2, width }}
    />
  );
}

function Chevron({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 text-muted-foreground transition-transform ${
        collapsed ? "-rotate-90" : ""
      }`}
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

// A count of one says nothing the row below it doesn't already say.
function Count({ value }: { value: number }) {
  if (value < 2) return null;
  return (
    <span className="shrink-0 font-mono text-[10.5px] tabular-nums text-ink2">
      {value.toLocaleString()}
    </span>
  );
}

/** A diamond on the spine: the marker for an interval, as opposed to a record. */
function IntervalMarker({ depth, height }: { depth: number; height: number }) {
  const size = depth === 0 ? 10 : 9;
  return (
    <span
      aria-hidden
      className="absolute rotate-45 rounded-[1px] bg-primary"
      style={{
        left: SPINE_X - size / 2,
        top: (height - size) / 2,
        width: size,
        height: size,
      }}
    />
  );
}

/** A bar across the spine: the marker for a subdivision of a group. */
function SubMarker({ height }: { height: number }) {
  return (
    <span
      aria-hidden
      className="absolute bg-node-line"
      style={{ left: SPINE_X - 4.5, top: height / 2, width: 9, height: 1 }}
    />
  );
}

// --- record row -------------------------------------------------------------

const ICON_INTERSECTIONS = new Set([
  HGIntersectionType.PartLeft,
  HGIntersectionType.PartRight,
  HGIntersectionType.Starts,
  HGIntersectionType.Ends,
  HGIntersectionType.Transit,
]);

interface RowContext {
  /** Level keys collapsed by the user; an interval level defaults to open. */
  toggled: Set<string>;
  toggle: (key: string) => void;
  expandedSections: Set<string>;
  expandSection: (key: string) => void;
  onNavigate: (interval: HGInterval) => void;
  /** The collection being browsed, if any; rows leave its badge off. */
  collectionID?: string;
  /** Title record id → how many records it stands for. See `titleRecords.ts`. */
  hiddenCounts: Map<string, number>;
  /** Collection id → its URL, for the badge on a title record's row. */
  collectionPaths: Map<string, string>;
  /** Collection id → collection, so a row resolves its badges without a query. */
  collectionsByID: Map<string, HGCollection>;
}

/**
 * The badge on a record that is also a collection.
 *
 * It always says "Collection", so the row reads the same whether or not
 * anything happened to be folded into it, and adds the count only when there is
 * one. The whole badge is the way into the collection: what it hides is one
 * click away, which is what makes hiding acceptable in the first place.
 */
function CollectionBadge({
  path,
  hidden,
}: {
  path: string | undefined;
  hidden: number;
}) {
  const label =
    hidden > 0 ? `Collection · ${hidden.toLocaleString()}` : "Collection";
  const title =
    hidden > 0
      ? `${hidden.toLocaleString()} ${hidden === 1 ? "record" : "records"} inside this one — open the collection`
      : "Open this record's collection";

  const className =
    "shrink-0 self-center whitespace-nowrap rounded border border-primary/40 bg-card px-2 py-0.5 font-mono text-[10.5px] text-muted-foreground";

  if (!path) {
    return (
      <span className={className} title={title}>
        {label}
      </span>
    );
  }

  return (
    <Link
      to={path}
      title={title}
      // The card underneath opens the preview; the badge is a different
      // destination and must not trigger it on the way.
      onClick={(event) => event.stopPropagation()}
      className={`${className} hover:border-primary hover:text-foreground`}
    >
      {label} →
    </Link>
  );
}

const RecordRow = memo(function RecordRow({
  node,
  depth,
  ctx,
}: {
  node: RecordNode;
  depth: number;
  ctx: RowContext;
}) {
  const dispatch = useAppDispatch();
  // Two booleans rather than the whole viewing record: with several thousand
  // rows on screen, subscribing each of them to the record itself meant every
  // preview open re-rendered the entire list.
  const selected = useAppSelector(selectIsViewingRecordID(node.record.id));
  const selectedHere = useAppSelector(selectIsSelectedRecordKey(node.key));

  const type = node.intersection.intersectionType;
  const icon = ICON_INTERSECTIONS.has(type) ? intersectionTypeIcon(type) : null;
  const dimmed = node.repeat || type === HGIntersectionType.Transit;

  // A record sitting inside its interval is a point on the line, so it gets a
  // filled dot. The ring is reserved for a record that *is* its interval.
  const contained = type === HGIntersectionType.Contained;

  // The node shrinks as the spine thins, so depth reads even out of context.
  const size = depth <= 0 ? 10 : depth === 1 ? 9 : depth === 2 ? 7 : 5;

  // This record is also a collection. Its marker stays that of any other
  // record — the "Collection" badge on the row already says what it is.
  const titleCollectionID = node.record.title_collection_id;
  const hidden = ctx.hiddenCounts.get(node.record.id) ?? 0;

  return (
    <div
      className="relative"
      style={{
        paddingLeft: CONTENT_X,
        paddingRight: CONTENT_R,
        paddingTop: ROW_PAD_Y,
        paddingBottom: ROW_PAD_Y,
      }}
    >
      <Spine depth={depth} />

      {/* The card stops short of the spine, so a stub carries the mark to it.
          Drawn under the mark, which knocks out its own stretch of it. */}
      <span
        aria-hidden
        className="pointer-events-none absolute bg-node-line"
        style={{
          left: SPINE_X,
          top: TITLE_CY,
          width: CONTENT_X - SPINE_X,
          height: 1,
        }}
      />

      {icon ? (
        <div
          className="absolute flex items-center justify-center bg-background text-muted-foreground"
          style={{
            left: SPINE_X - 7.5,
            top: TITLE_CY - 7.5,
            width: 15,
            height: 15,
          }}
        >
          {icon}
        </div>
      ) : (
        <div
          className={`absolute rounded-full ${
            selected
              ? "border-[1.5px] border-primary bg-primary"
              : contained || depth >= 3
                ? "bg-muted-foreground"
                : depth <= 0
                  ? "border-2 border-primary/60 bg-background"
                  : "border-[1.5px] border-node-line bg-background"
          }`}
          style={{
            left: SPINE_X - size / 2,
            top: TITLE_CY - size / 2,
            width: size,
            height: size,
          }}
        />
      )}

      <div
        onClick={() => {
          // Only the row that opened it closes it again.
          dispatch(
            setViewingRecordFromRow(
              selected && selectedHere
                ? null
                : { record: serializeRecord(node.record), key: node.key },
            ),
          );
        }}
        title={node.repeat ? "Also shown earlier" : undefined}
        className={`relative ${recordCardClass(selected)}`}
      >
        <RecordBody
          record={node.record}
          currentCollectionID={ctx.collectionID}
          collectionsByID={ctx.collectionsByID}
          dimmed={dimmed}
          trailing={
            titleCollectionID ? (
              <CollectionBadge
                path={ctx.collectionPaths.get(titleCollectionID)}
                hidden={hidden}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
});

// --- capped record list -----------------------------------------------------

const RecordList = memo(function RecordList({
  sectionKey,
  records,
  depth,
  ctx,
}: {
  sectionKey: string;
  records: RecordNode[];
  depth: number;
  ctx: RowContext;
}) {
  // Without virtualization the DOM is the budget: long runs reveal in pages.
  const expanded = ctx.expandedSections.has(sectionKey);
  const shown = expanded ? records : records.slice(0, SECTION_PAGE);
  const hidden = records.length - shown.length;

  return (
    <>
      {shown.map((record) => (
        <RecordRow key={record.key} node={record} depth={depth} ctx={ctx} />
      ))}
      {hidden > 0 && (
        <div
          className="relative"
          style={{ paddingLeft: CONTENT_X, paddingRight: CONTENT_R }}
        >
          <Spine depth={depth} />
          <button
            type="button"
            onClick={() => ctx.expandSection(sectionKey)}
            className="flex h-[28px] items-center font-sans text-[11.5px] text-muted-foreground hover:text-foreground"
          >
            Show {hidden.toLocaleString()} more
          </button>
        </div>
      )}
    </>
  );
});

// --- buckets ----------------------------------------------------------------

/**
 * A bucket is an ordering, not a section. Each row already carries the glyph
 * for how it meets its interval — starts, ends, runs through — so heading the
 * runs as well would say it twice.
 */
const BucketRows = memo(function BucketRows({
  level,
  kind,
  ctx,
}: {
  level: LevelNode;
  kind: BucketKind;
  ctx: RowContext;
}) {
  const records = level.buckets[kind];
  if (records.length === 0) return null;

  return (
    <RecordList
      sectionKey={`${level.key}#${kind}`}
      records={records}
      depth={level.depth}
      ctx={ctx}
    />
  );
});

// --- gaps -------------------------------------------------------------------

const GAP_H = 24;

const GapRow = memo(function GapRow({
  gap,
  depth,
}: {
  gap: GapNode;
  depth: number;
}) {
  const unit =
    gap.count === 1 ? getSingular(gap.from.it) : getPlural(gap.from.it);
  const span =
    gap.count === 1
      ? safeShortName(gap.from)
      : `${safeShortName(gap.from)}–${safeShortName(gap.to)}`;

  return (
    <div
      className="relative flex items-center"
      style={{ height: GAP_H, paddingLeft: CONTENT_X, paddingRight: CONTENT_R }}
      title={`Nothing recorded · ${span}`}
    >
      {/* Nothing happened here, so the line goes dotted rather than absent. */}
      {/* The dashes are the indicator — a glyph on top of them would only
          compete with the subdivision markers. */}
      <Spine depth={depth} dashed />
      <span className="font-mono text-[10px] tracking-[.03em] text-muted-foreground">
        {span} · {gap.count.toLocaleString()} {unit}
      </span>
    </div>
  );
});

// --- levels -----------------------------------------------------------------

const LevelBody = memo(function LevelBody({
  level,
  ctx,
}: {
  level: LevelNode;
  ctx: RowContext;
}) {
  // Records that frame the interval open the level, its subdivisions fill the
  // middle, and records that close or outlast it come last.
  return (
    <>
      <BucketRows level={level} kind="exact" ctx={ctx} />
      <BucketRows level={level} kind="starts" ctx={ctx} />

      {level.children.map((child) =>
        isLevelNode(child) ? (
          <LevelSection key={child.key} level={child} ctx={ctx} />
        ) : (
          <GapRow key={child.key} gap={child} depth={level.depth + 1} />
        ),
      )}

      {level.leaves.length > 0 && (
        <RecordList
          sectionKey={`${level.key}#leaves`}
          records={level.leaves}
          depth={level.depth}
          ctx={ctx}
        />
      )}

      <BucketRows level={level} kind="ends" ctx={ctx} />
      <BucketRows level={level} kind="transit" ctx={ctx} />
    </>
  );
});

const LevelSection = memo(function LevelSection({
  level,
  ctx,
}: {
  level: LevelNode;
  ctx: RowContext;
}) {
  const collapsed = ctx.toggled.has(level.key);
  const depth = level.depth;
  const height = levelHeaderHeight(depth);
  const isGroup = depth === 1;

  return (
    <section className="relative">
      {/* The whole header line is the collapse target — only the label, which
          navigates, opts out of it. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-label={
          collapsed ? `Expand ${level.name}` : `Collapse ${level.name}`
        }
        onClick={() => ctx.toggle(level.key)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          ctx.toggle(level.key);
        }}
        className="group relative flex cursor-pointer items-center gap-[6px] bg-background pr-[18px] hover:bg-row-hover"
        style={{
          height,
          paddingLeft: CONTENT_X,
          position: "sticky",
          top: levelHeaderTop(depth),
          zIndex: levelZ(depth),
        }}
      >
        {/* The header is where the spine changes gauge. */}
        <Spine depth={depth} />
        {isGroup ? (
          <IntervalMarker depth={depth} height={height} />
        ) : (
          <SubMarker height={height} />
        )}

        {/* The label navigates; everything else on the line collapses. */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            ctx.onNavigate(level.interval);
          }}
          title={`Go to ${level.name}`}
          className={
            isGroup
              ? "shrink-0 whitespace-nowrap font-serif text-[13px] font-semibold tracking-[.01em] text-accent-foreground hover:underline"
              : "shrink-0 whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[.06em] text-ink2 hover:text-foreground"
          }
        >
          {isGroup ? level.name : level.shortName}
        </button>

        <span className="-my-1 shrink-0 px-[2px] py-1">
          <Chevron collapsed={collapsed} />
        </span>

        <div
          className={`h-px flex-1 shrink ${isGroup ? "bg-border" : "bg-node-line/60"}`}
        />
        <Count value={level.total} />
      </div>

      {!collapsed && <LevelBody level={level} ctx={ctx} />}
    </section>
  );
});

// --- entry point ------------------------------------------------------------

interface RecordsTimelineProps {
  tree: RecordTree;
  toggled: Set<string>;
  toggle: (key: string) => void;
  onNavigate: (interval: HGInterval) => void;
  /** The collection being browsed, if any; rows leave its badge off. */
  collectionID?: string;
  /** Title record id → how many records it stands for. See `titleRecords.ts`. */
  hiddenCounts: Map<string, number>;
  /** How many records are folded away in total, for the scope header. */
  hiddenTotal: number;
}

export default function RecordsTimeline({
  tree,
  toggled,
  toggle,
  onNavigate,
  collectionID,
  hiddenCounts,
  hiddenTotal,
}: RecordsTimelineProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const collections = useCollections();

  const expandSection = useCallback(
    (key: string) =>
      setExpandedSections((previous) => new Set(previous).add(key)),
    [],
  );

  // Built once for the list rather than per row: every title record's badge
  // needs a URL, and collectionPath() has to be given the whole collection.
  const collectionPaths = useMemo(
    () => new Map(collections.map((c) => [c.id, collectionPath(c)])),
    [collections],
  );
  // Likewise the index every row used to rebuild for itself, through a query
  // subscription and a filter over the whole collection list.
  const collectionsByID = useMemo(
    () => indexCollections(collections),
    [collections],
  );

  // Deliberately excludes anything that changes on selection: `ctx` is a prop
  // of every memoised row, so a new identity here re-renders the whole list.
  // Selection is read per row straight off the store instead.
  const ctx: RowContext = useMemo(
    () => ({
      toggled,
      toggle,
      expandedSections,
      expandSection,
      onNavigate,
      collectionID,
      hiddenCounts,
      collectionPaths,
      collectionsByID,
    }),
    [
      toggled,
      toggle,
      expandedSections,
      expandSection,
      onNavigate,
      collectionID,
      hiddenCounts,
      collectionPaths,
      collectionsByID,
    ],
  );

  return (
    <div className="relative pb-24">
      <div
        className="relative flex items-center gap-2 border-b border-border bg-background/95 pr-[18px] backdrop-blur"
        style={{
          height: SCOPE_H,
          paddingLeft: CONTENT_X,
          position: "sticky",
          top: 0,
          zIndex: levelZ(0),
        }}
      >
        <Spine depth={0} />
        <IntervalMarker depth={0} height={SCOPE_H} />
        <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
          {tree.total.toLocaleString()}{" "}
          {tree.total === 1 ? "record" : "records"}
          {/* The count above is what is on screen. Saying how many more are
              folded into the records heading their collections keeps the two
              numbers from looking like a discrepancy. */}
          {hiddenTotal > 0 && (
            <span className="text-ink2">
              {" · "}
              {hiddenTotal.toLocaleString()} inside
            </span>
          )}
        </span>
        <span className="flex-1" />
      </div>

      <LevelBody level={tree.root} ctx={ctx} />
    </div>
  );
}
